import React, { useState } from 'react';
import { History, RotateCcw, GitCompare, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Badge, Button, SkeletonList } from '../../components/ui';
import {
  useWorkflowVersions,
  useWorkflowVersionDiff,
  useRestoreWorkflowVersion,
} from '../../hooks/useWorkflowVersions.js';
import type { WorkflowVersion } from '../../generated/api/types.gen.js';

interface WorkflowVersionHistoryProps {
  workflowId: number;
  onClose: () => void;
  onRestored?: () => void;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function changeBadge(type: string) {
  switch (type) {
    case 'added':
      return <Badge variant="success">added</Badge>;
    case 'removed':
      return <Badge variant="danger">removed</Badge>;
    case 'modified':
      return <Badge variant="warning">modified</Badge>;
    default:
      return null;
  }
}

export const WorkflowVersionHistory: React.FC<WorkflowVersionHistoryProps> = ({ workflowId, onClose, onRestored }) => {
  const { data, isLoading } = useWorkflowVersions(workflowId);
  const restoreMutation = useRestoreWorkflowVersion();

  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null);
  const [diffPair, setDiffPair] = useState<[number, number] | null>(null);

  const versions = data?.versions ?? [];

  const handleRestore = async (version: number) => {
    await restoreMutation.mutateAsync({ workflowId, version });
    setConfirmRestore(null);
    onRestored?.();
  };

  const handleDiffToggle = (version: number) => {
    if (!diffPair) {
      // Start diff selection: compare with previous version
      const idx = versions.findIndex((v) => v.version === version);
      const prev = versions[idx + 1];
      if (prev) {
        setDiffPair([prev.version, version]);
      }
    } else {
      setDiffPair(null);
    }
  };

  return (
    <div className="bg-surface rounded-lg border flex flex-col max-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <h3 className="flex items-center gap-2 font-semibold text-heading text-sm">
          <History size={14} />
          Version history
        </h3>
        <Button variant="ghost" size="sm" iconOnly aria-label="Close version history" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-4">
            <SkeletonList count={3} label="Loading versions" />
          </div>
        )}

        {!isLoading && versions.length === 0 && (
          <div className="text-center py-8 text-muted text-sm">
            No version history yet. Versions are created when you save changes.
          </div>
        )}

        {/* Diff view */}
        {diffPair && (
          <DiffView
            workflowId={workflowId}
            versionA={diffPair[0]}
            versionB={diffPair[1]}
            onClose={() => setDiffPair(null)}
          />
        )}

        {/* Version list */}
        {!diffPair &&
          versions.map((v) => (
            <VersionRow
              key={v.id}
              version={v}
              expanded={expandedVersion === v.version}
              onToggle={() => setExpandedVersion(expandedVersion === v.version ? null : v.version)}
              onRestore={() => setConfirmRestore(v.version)}
              onDiff={() => handleDiffToggle(v.version)}
              showDiff={versions.length > 1}
            />
          ))}
      </div>

      {/* Restore confirmation */}
      {confirmRestore !== null && (
        <div className="border-t px-4 py-3 bg-surface-alt">
          <p className="text-sm text-body mb-2">
            Restore to version <span className="font-semibold text-heading">v{confirmRestore}</span>? This will create a
            new version with the restored content.
          </p>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleRestore(confirmRestore)}
              loading={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? 'Restoring...' : 'Confirm restore'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmRestore(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Version Row
// ==========================================

interface VersionRowProps {
  version: WorkflowVersion;
  expanded: boolean;
  onToggle: () => void;
  onRestore: () => void;
  onDiff: () => void;
  showDiff: boolean;
}

const VersionRow: React.FC<VersionRowProps> = ({ version, expanded, onToggle, onRestore, onDiff, showDiff }) => {
  const summary = version.changeSummary || `Version ${version.version}`;

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-alt transition-colors ">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-expanded={expanded}
          className="h-auto min-w-0 flex-1 items-start justify-start gap-2 p-0 text-left font-normal"
        >
          <span className="mt-0.5 text-faint">{expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-accent-text font-medium">v{version.version}</span>
              <span className="text-sm text-heading truncate">{summary}</span>
              <span className="text-xs text-faint whitespace-nowrap">{timeAgo(version.createdAt)}</span>
            </div>
          </div>
        </Button>
        <div className="flex items-center gap-1">
          {showDiff && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Compare with previous version"
              title="Compare with previous version"
              onClick={onDiff}
            >
              <GitCompare size={13} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Restore this version"
            title="Restore this version"
            onClick={onRestore}
          >
            <RotateCcw size={13} />
          </Button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-3 pl-10">
          <div className="text-xs text-muted mb-1">
            <span className="text-faint">State:</span> {version.state}
          </div>
          <div className="text-xs text-faint mt-1.5">{new Date(version.createdAt).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Diff View
// ==========================================

interface DiffViewProps {
  workflowId: number;
  versionA: number;
  versionB: number;
  onClose: () => void;
}

const DiffView: React.FC<DiffViewProps> = ({ workflowId, versionA, versionB, onClose }) => {
  const { data, isLoading } = useWorkflowVersionDiff(workflowId, versionA, versionB);

  return (
    <div className="border-b">
      <div className="flex items-center justify-between px-4 py-2 bg-surface-alt">
        <span className="text-xs text-muted">
          <GitCompare size={12} className="inline mr-1" />
          Comparing <span className="font-mono text-heading">v{versionA}</span> &rarr;{' '}
          <span className="font-mono text-heading">v{versionB}</span>
        </span>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      {isLoading && (
        <div className="p-4">
          <SkeletonList count={2} label="Computing diff" />
        </div>
      )}

      {data && data.changes.length === 0 && (
        <div className="text-center py-4 text-muted text-xs">No differences found.</div>
      )}

      {data && data.changes.length > 0 && (
        <div className="px-4 py-2 space-y-2">
          {data.changes.map((change, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              {changeBadge(change.type)}
              <span className="text-body">{change.stepName || change.stepId}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
